import { getCsrfToken } from "@/integrations/php/client";
import type { ServiceType } from "@/context/LeadContext";

export type AssignmentRange = "7d" | "30d" | "90d" | "1y" | "all";
export type AssignmentService = ServiceType | "all";

export interface AssignmentSummary {
  total: number;
  assigned: number;
  unassigned: number;
  new_leads: number;
  pending: number;
  information: number;
  success: number;
  assigned_success: number;
  viewed: number;
}

export interface AssignmentPartnerMetric {
  id: string;
  name: string;
  location: string;
  active: boolean;
  radius: number;
  total: number;
  new_leads: number;
  pending: number;
  information: number;
  success: number;
  viewed: number;
  last_lead: string | null;
}

export interface AssignmentServiceMetric {
  service: string;
  total: number;
  assigned: number;
  unassigned: number;
}

export interface AssignmentMetrics {
  summary: AssignmentSummary;
  partners: AssignmentPartnerMetric[];
  services: AssignmentServiceMetric[];
  filters: { range: AssignmentRange; service: AssignmentService };
  generated_at: string;
}

const numericFields = ["total", "assigned", "unassigned", "new_leads", "pending", "information", "success", "assigned_success", "viewed"] as const;

const toNumber = (value: unknown): number => Number(value || 0);

export async function fetchAssignmentMetrics(
  range: AssignmentRange,
  service: AssignmentService,
): Promise<AssignmentMetrics> {
  const params = new URLSearchParams({ range, service });
  const response = await fetch(`/api/admin/assignment-metrics.php?${params.toString()}`, {
    credentials: "include",
    headers: { "X-CSRF-Token": await getCsrfToken() },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error?.message || "Δεν φορτώθηκαν τα στοιχεία αναθέσεων.");

  const data = body.data || {};
  const rawSummary = data.summary || {};
  const summary = Object.fromEntries(
    numericFields.map((field) => [field, toNumber(rawSummary[field])]),
  ) as unknown as AssignmentSummary;

  return {
    summary,
    partners: Array.isArray(data.partners)
      ? data.partners.map((partner: Record<string, unknown>) => ({
          id: String(partner.id || ""),
          name: String(partner.name || ""),
          location: String(partner.location || ""),
          active: Boolean(partner.active),
          radius: toNumber(partner.radius),
          total: toNumber(partner.total),
          new_leads: toNumber(partner.new_leads),
          pending: toNumber(partner.pending),
          information: toNumber(partner.information),
          success: toNumber(partner.success),
          viewed: toNumber(partner.viewed),
          last_lead: partner.last_lead ? String(partner.last_lead) : null,
        }))
      : [],
    services: Array.isArray(data.services)
      ? data.services.map((row: Record<string, unknown>) => ({
          service: String(row.service || "άλλο"),
          total: toNumber(row.total),
          assigned: toNumber(row.assigned),
          unassigned: toNumber(row.unassigned),
        }))
      : [],
    filters: {
      range: (data.filters?.range || range) as AssignmentRange,
      service: (data.filters?.service || service) as AssignmentService,
    },
    generated_at: String(data.generated_at || ""),
  };
}
